import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, CheckCircle2, Upload } from 'lucide-react';
import { submitCustomerFeedback } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface FeedbackPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    if (!description || description.length < 10) {
      setError('Please provide a description of at least 10 characters.');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('description', description);
      formData.append('pageUrl', window.location.href);
      formData.append('userAgent', navigator.userAgent);
      if (user?.id) {
        formData.append('userId', user.id);
      }
      if (user?.email) {
        formData.append('userName', user.email.split('@')[0]); // Use email prefix as name
      }
      if (screenshot) {
        formData.append('screenshot', screenshot);
      }

      const response = await submitCustomerFeedback(formData);

      if (response.success) {
        setSubmitSuccess(true);
        toast.success('Feedback submitted successfully!', { icon: '✅' });
        setTimeout(() => {
          onClose();
          setEmail(user?.email || '');
          setDescription('');
          setScreenshot(null);
          setSubmitSuccess(false);
        }, 3000);
      } else {
        setError(response.error || 'Failed to submit feedback. Please try again.');
        toast.error(response.error || 'Failed to submit feedback.', { icon: '❌' });
      }
    } catch (err: any) {
      console.error('Feedback submission error:', err);
      setError(err.response?.data?.error || 'An unexpected error occurred.');
      toast.error(err.response?.data?.error || 'An unexpected error occurred.', { icon: '❌' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setError('Screenshot file size cannot exceed 5MB.');
        setScreenshot(null);
        return;
      }
      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        setError('Only PNG and JPG images are allowed.');
        setScreenshot(null);
        return;
      }
      setScreenshot(file);
      setError(null);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100] bg-black bg-opacity-50 transition-opacity ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className={`fixed right-0 top-0 h-full w-full sm:w-[450px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Customer Support</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close feedback panel"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {submitSuccess ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-green-600">
                <CheckCircle2 className="w-16 h-16 mb-4" />
                <h3 className="text-xl font-semibold mb-2">Thank You for Your Feedback!</h3>
                <p className="text-gray-600">We appreciate you taking the time to help us improve.</p>
                <p className="text-sm text-gray-500 mt-4">This panel will close automatically.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <p className="text-gray-600">
                  We'd love to hear from you! Please share any issues, suggestions, or questions you have.
                </p>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    How can we help? *
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-y"
                    placeholder="Describe your issue or suggestion..."
                    minLength={10}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {description.length} /10 characters minimum
                  </p>
                </div>

                <div>
                  <label htmlFor="screenshot" className="block text-sm font-medium text-gray-700 mb-1">
                    Screenshot (optional)
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                    <div className="space-y-1 text-center">
                      {screenshot ? (
                        <div className="flex items-center justify-center text-sm text-gray-600">
                          <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" />
                          <span>{screenshot.name}</span>
                          <button
                            type="button"
                            onClick={() => setScreenshot(null)}
                            className="ml-2 text-red-500 hover:text-red-700"
                          >
                            (Remove)
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className="mx-auto h-12 w-12 text-gray-400" />
                          <div className="flex text-sm text-gray-600">
                            <label
                              htmlFor="file-upload"
                              className="relative cursor-pointer rounded-md bg-white font-medium text-orange-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-orange-500 focus-within:ring-offset-2 hover:text-orange-500"
                            >
                              <span>Upload a file</span>
                              <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                className="sr-only"
                                onChange={handleFileChange}
                                accept="image/png, image/jpeg"
                              />
                            </label>
                            <p className="pl-1">or drag and drop</p>
                          </div>
                          <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-600 text-center font-medium">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold text-lg transition-all duration-300 ${
                    !isSubmitting
                      ? 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin w-5 h-5" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Feedback
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPanel;

